import { type ParticipationTask } from "@mg/schemas/src/christo/participationTask";
import { FileText } from "@phosphor-icons/react";
import { Heading, HoverCard, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { useMemo, useState } from "react";

import {
  ApprovalRequestForm,
  type ApprovalRequestAction,
  type ApprovalRequestAssetVisual,
  parseAssetLabel,
} from "./ApprovalRequestForm";

import {
  useAddProjectApprovalNote,
  useDeclineProjectInvitation,
  useParticipationTasks,
  useProjectReviews,
  useRecordProjectPosition,
} from "../../../utils/queries/askPunttProjects";
import {
  formatListCardRelativeDate,
  LIST_CARD_RELATIVE_TIME_CLASS,
} from "../utils/listCardTimestamps";

const INITIAL_VISIBLE_COUNT = 5;
export const REVIEW_HOVER_OPEN_DELAY_MS = 200;
export const REVIEW_HOVER_CLOSE_DELAY_MS = 150;

/** Outline-style Review control — puntt tokens only; never white-on-white hover. */
export function reviewButtonClassName(isOpen = false): string {
  return cx(
    "inline-flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-md border px-3 text-[13px] font-medium no-underline outline-none transition-colors",
    "border-puntt-neutral-gray-6 bg-base-white text-puntt-neutral-gray-12",
    "hover:border-puntt-accent-7 hover:bg-puntt-blue-2 hover:text-puntt-neutral-gray-12",
    "focus-visible:ring-2 focus-visible:ring-puntt-accent-8",
    isOpen &&
      "border-puntt-accent-7 bg-puntt-blue-2 text-puntt-neutral-gray-12",
  );
}

export function participationKindLabel(
  kind: ParticipationTask["kind"],
): string {
  return kind === "invited" ? "Invited" : "Suggested";
}

export function homeTaskReviewKey(task: ParticipationTask): string {
  return `${task.projectId}-${task.versionId}`;
}

export function homeTaskRowTestId(task: ParticipationTask): string {
  return `ask-puntt-home-task-row-${task.projectId}-${task.versionId}`;
}

export function homeTaskRowLinkTestId(task: ParticipationTask): string {
  return `ask-puntt-home-task-row-link-${task.projectId}-${task.versionId}`;
}

export function homeTaskReviewTestId(task: ParticipationTask): string {
  return `ask-puntt-home-task-review-${task.projectId}-${task.versionId}`;
}

export function homeTaskReviewPopoverTestId(task: ParticipationTask): string {
  return `ask-puntt-home-task-review-popover-${task.projectId}-${task.versionId}`;
}

export function resolveHomeTaskTitle(task: ParticipationTask): string {
  const label = task.assetLabel?.trim();
  return label && label.length > 0 ? label : "Review request";
}

export function resolveHomeTaskMeta(task: ParticipationTask): string {
  const relative = formatListCardRelativeDate(task.openedAt ?? task.updatedAt);
  const parts = [participationKindLabel(task.kind), task.projectName];
  if (relative) parts.push(relative);
  return parts.join(" · ");
}

export function resolveHomeTaskRowAriaLabel(task: ParticipationTask): string {
  const title = resolveHomeTaskTitle(task);
  return `Open ${title} in ${task.projectName}`;
}

function HomeTaskReviewHoverCard({
  task,
  onDenied,
  onPostDenySettled,
}: {
  task: ParticipationTask;
  onDenied: (task: ParticipationTask) => void;
  onPostDenySettled: (task: ParticipationTask) => void;
}) {
  const title = resolveHomeTaskTitle(task);
  const [open, setOpen] = useState(false);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);

  const reviewsQuery = useProjectReviews(task.projectId, { enabled: open });
  const recordPosition = useRecordProjectPosition(task.projectId);
  const addNote = useAddProjectApprovalNote(task.projectId);
  const declineInvitation = useDeclineProjectInvitation(task.projectId);

  const assetsByVersionId = useMemo(() => {
    const reviews = reviewsQuery.data?.reviews ?? [];
    const review =
      reviews.find(
        (r) =>
          r.fileId === task.fileId ||
          (task.ticketId != null && r.ticketId === task.ticketId),
      ) ?? null;
    const parsed = parseAssetLabel(task.assetLabel);
    const map: Record<string, ApprovalRequestAssetVisual> = {
      [task.versionId]: {
        name: review?.name ?? parsed.name ?? "Review asset",
        versionNumber: review?.versionNumber ?? parsed.versionNumber,
        thumbnailUrl: review?.thumbnailUrl ?? null,
      },
    };
    return map;
  }, [
    reviewsQuery.data?.reviews,
    task.assetLabel,
    task.fileId,
    task.ticketId,
    task.versionId,
  ]);

  const submit = async (input: {
    task: ParticipationTask;
    action: ApprovalRequestAction;
    noteBody?: string;
  }) => {
    const { task: activeTask, action, noteBody } = input;
    setBusyVersionId(activeTask.versionId);
    const idempotencyKey = `app:home-hover:${action}:${activeTask.projectId}:${activeTask.versionId}:${Date.now()}`;
    try {
      if (action === "not_needed") {
        await declineInvitation.mutateAsync({
          versionId: activeTask.versionId,
          fileId: activeTask.fileId,
          ticketId: activeTask.ticketId,
          idempotencyKey,
        });
        setOpen(false);
      } else if (action === "note_added") {
        const body = noteBody?.trim();
        if (!body) return;
        await addNote.mutateAsync({
          versionId: activeTask.versionId,
          fileId: activeTask.fileId,
          noteBody: body,
          ticketId: activeTask.ticketId,
          idempotencyKey,
        });
        setOpen(false);
      } else if (action === "changes_requested") {
        onDenied(activeTask);
        await recordPosition.mutateAsync({
          versionId: activeTask.versionId,
          fileId: activeTask.fileId,
          position: action,
          ticketId: activeTask.ticketId,
          idempotencyKey,
        });
        // Keep the hover open so the post-deny note step can complete.
        setOpen(true);
      } else {
        await recordPosition.mutateAsync({
          versionId: activeTask.versionId,
          fileId: activeTask.fileId,
          position: action,
          ticketId: activeTask.ticketId,
          idempotencyKey,
        });
        setOpen(false);
      }
    } finally {
      setBusyVersionId(null);
    }
  };

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={REVIEW_HOVER_OPEN_DELAY_MS}
      closeDelay={REVIEW_HOVER_CLOSE_DELAY_MS}
    >
      <HoverCard.Trigger>
        <Link
          to={task.ctaPath}
          className={reviewButtonClassName(open)}
          data-testid={homeTaskReviewTestId(task)}
          aria-label={`Review ${title}`}
          data-review-open={open ? "true" : "false"}
        >
          Review
        </Link>
      </HoverCard.Trigger>
      <HoverCard.Content
        side="left"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        className="z-50 max-h-[min(70vh,420px)] w-[min(92vw,360px)] overflow-y-auto border border-puntt-neutral-gray-6 bg-base-white p-3 shadow-md"
        data-testid={homeTaskReviewPopoverTestId(task)}
        aria-label={`Quick review for ${title}`}
      >
        <ApprovalRequestForm
          variant="compact"
          tasks={[task]}
          busyVersionId={busyVersionId}
          assetsByVersionId={assetsByVersionId}
          onRespond={(input) => {
            void submit(input);
          }}
          onPostDenySettled={() => {
            onPostDenySettled(task);
            setOpen(false);
          }}
        />
        <div className="mt-2 border-t border-puntt-neutral-gray-4 pt-2">
          <Link
            to={task.ctaPath}
            className="text-[12px] font-medium text-puntt-accent-11 hover:text-puntt-accent-12"
            data-testid={`ask-puntt-home-task-open-project-${task.projectId}-${task.versionId}`}
            aria-label={`Open ${title} in project`}
          >
            Open in project
          </Link>
        </div>
      </HoverCard.Content>
    </HoverCard.Root>
  );
}

function HomeTaskRow({
  task,
  onDenied,
  onPostDenySettled,
}: {
  task: ParticipationTask;
  onDenied: (task: ParticipationTask) => void;
  onPostDenySettled: (task: ParticipationTask) => void;
}) {
  const title = resolveHomeTaskTitle(task);
  const meta = resolveHomeTaskMeta(task);
  const rowAriaLabel = resolveHomeTaskRowAriaLabel(task);

  return (
    <div
      className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 py-3 last:border-b-0"
      data-testid={homeTaskRowTestId(task)}
      aria-label={rowAriaLabel}
    >
      <Link
        to={task.ctaPath}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 no-underline outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-8"
        data-testid={homeTaskRowLinkTestId(task)}
        aria-label={rowAriaLabel}
      >
        <span
          className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
          aria-hidden="true"
          data-testid={`${homeTaskRowTestId(task)}-icon`}
        >
          <FileText size={18} weight="fill" />
          <span
            className="bg-puntt-yellow-9 absolute bottom-0.5 right-0.5 size-2 rounded-full border border-base-white"
            aria-hidden="true"
          />
        </span>

        <div className="min-w-0 flex-1">
          <Text
            as="p"
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
            data-testid={`${homeTaskRowTestId(task)}-title`}
          >
            {title}
          </Text>
          <p
            className={LIST_CARD_RELATIVE_TIME_CLASS}
            data-testid={`${homeTaskRowTestId(task)}-meta`}
          >
            {meta}
          </p>
        </div>
      </Link>

      <HomeTaskReviewHoverCard
        task={task}
        onDenied={onDenied}
        onPostDenySettled={onPostDenySettled}
      />
    </div>
  );
}

/**
 * Home inbox: open participation tasks for the current user across all projects.
 * Renders null when there are no open tasks (empty Active inbox).
 */
export function HomeTaskList() {
  const tasksQuery = useParticipationTasks({ status: "open" });
  const openTasks = tasksQuery.data?.tasks ?? [];
  const [expanded, setExpanded] = useState(false);
  // Keep denied rows mounted briefly so the post-deny note step can finish
  // after the open-task query drops them.
  const [stickyPostDeny, setStickyPostDeny] = useState<
    Record<string, ParticipationTask>
  >({});

  const tasks = useMemo(() => {
    const byKey = new Map(
      openTasks.map((task) => [homeTaskReviewKey(task), task] as const),
    );
    for (const [key, task] of Object.entries(stickyPostDeny)) {
      if (!byKey.has(key)) byKey.set(key, task);
    }
    return Array.from(byKey.values());
  }, [openTasks, stickyPostDeny]);

  const visibleTasks = useMemo(() => {
    if (expanded || tasks.length <= INITIAL_VISIBLE_COUNT) return tasks;
    return tasks.slice(0, INITIAL_VISIBLE_COUNT);
  }, [expanded, tasks]);

  const hiddenCount = tasks.length - INITIAL_VISIBLE_COUNT;

  const handleDenied = (task: ParticipationTask) => {
    setStickyPostDeny((prev) => ({
      ...prev,
      [homeTaskReviewKey(task)]: task,
    }));
  };

  const handlePostDenySettled = (task: ParticipationTask) => {
    const key = homeTaskReviewKey(task);
    setStickyPostDeny((prev) => {
      if (prev[key] == null) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (tasks.length === 0) return null;

  return (
    <section
      className="mt-6 w-full"
      data-testid="ask-puntt-home-task-list"
      aria-label="Active review tasks"
    >
      <Heading
        as="h2"
        size="3"
        weight="bold"
        className="mb-1 text-puntt-neutral-gray-12"
        data-testid="ask-puntt-home-task-list-active"
      >
        Active
      </Heading>

      <div className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white px-4">
        {visibleTasks.map((task) => (
          <HomeTaskRow
            key={homeTaskReviewKey(task)}
            task={task}
            onDenied={handleDenied}
            onPostDenySettled={handlePostDenySettled}
          />
        ))}
      </div>

      {!expanded && hiddenCount > 0 ? (
        <button
          type="button"
          className="mt-2 cursor-pointer text-[13px] font-medium text-puntt-neutral-gray-11 hover:text-puntt-neutral-gray-12"
          data-testid="ask-puntt-home-task-show-more"
          aria-label={`Show ${hiddenCount} more active tasks`}
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      ) : null}
    </section>
  );
}
